<script setup lang="ts">
import {computed} from "vue";
import {Dayjs} from "dayjs";
import LegacyAccess from "@/core/interfaces/Access";
import {MemberDay, dayCoverage} from "@/planning/lib/weekPlanning";
import {CalendarOff, ShieldAlert, ShieldCheck, TriangleAlert} from "@lucide/vue";

const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const {anchor, members, resolveDay} = defineProps<{
    /** Any date inside the displayed month. */
    anchor: Dayjs;
    members: LegacyAccess[];
    resolveDay: (member: LegacyAccess, date: Dayjs) => MemberDay;
}>();

defineEmits<{
    (event: 'showAbsences'): void;
}>();

interface MonthCell {
    date: Dayjs | null;
    status?: 'covered' | 'fragile' | 'uncovered' | 'closed';
    headcount?: number;
}

const cells = computed<MonthCell[]>(() => {
    const first = anchor.startOf('month');
    const leading = (first.day() + 6) % 7;
    const daysInMonth = anchor.daysInMonth();

    return [
        ...Array.from({length: leading}, () => ({date: null})),
        ...Array.from({length: daysInMonth}, (_, index) => {
            const date = first.add(index, 'day');
            const coverage = dayCoverage(members.map(member => resolveDay(member, date)));

            return {date, status: coverage.status, headcount: coverage.headcount};
        }),
    ];
});

const cellClass = (cell: MonthCell): string => {
    switch (cell.status) {
        case 'covered':
            return 'border-green-200 bg-green-50/60';
        case 'fragile':
            return 'border-amber-200 bg-amber-50/60';
        case 'uncovered':
            return 'border-red-200 bg-red-50/60';
        default:
            return 'border-gray-100 bg-white';
    }
};
</script>

<template>
  <div class="mb-2.5 flex flex-wrap items-center gap-2.5">
    <!-- Only the month is capitalized: on the whole line, `capitalize`
         title-cased the label, which the charte forbids. -->
    <div class="flex-1 text-xs text-gray-400">
      <span class="capitalize">{{ anchor.format('MMMM YYYY') }}</span> — couverture pharmacien par jour
    </div>
    <button
      type="button"
      class="flex h-7 items-center gap-1.5 rounded-lg border border-solid border-gray-200 bg-white px-2.5 text-xs font-semibold text-primary hover:border-primary"
      @click="$emit('showAbsences')"
    >
      <CalendarOff class="size-3.5" />
      Voir les absences
    </button>
  </div>
  <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <div class="mb-1.5 grid grid-cols-7 gap-1.5">
      <div v-for="day in WEEK_DAYS" :key="day" class="text-center text-[10px] font-semibold text-gray-400">
        {{ day }}
      </div>
    </div>
    <div class="grid grid-cols-7 gap-1.5">
      <div
        v-for="(cell, index) in cells"
        :key="index"
        class="flex min-h-16 flex-col gap-0.5 rounded-lg border p-1.5"
        :class="cell.date ? cellClass(cell) : 'border-transparent'"
      >
        <template v-if="cell.date">
          <div class="flex items-center gap-1">
            <span class="flex-1 text-[11px] font-semibold text-gray-700">{{ cell.date.date() }}</span>
            <ShieldCheck v-if="cell.status === 'covered'" class="size-3.5 text-green-600" />
            <TriangleAlert v-else-if="cell.status === 'fragile'" class="size-3.5 text-amber-600" />
            <ShieldAlert v-else-if="cell.status === 'uncovered'" class="size-3.5 text-red-500" />
          </div>
          <template v-if="cell.status !== 'closed'">
            <div class="text-[8.5px] leading-tight text-gray-400">
              {{ cell.headcount }} pers.
            </div>
            <div
              class="text-[8px] font-semibold"
              :class="cell.status === 'covered' ? 'text-green-700' : cell.status === 'fragile' ? 'text-amber-700' : 'text-red-600'"
            >
              {{ cell.status === 'covered' ? 'Conforme' : cell.status === 'fragile' ? 'Couverture fragile' : 'Sans pharmacien' }}
            </div>
          </template>
          <div v-else class="text-[8.5px] text-gray-300">
            Fermé
          </div>
        </template>
      </div>
    </div>
    <div class="mt-3 flex flex-wrap gap-4 text-[10.5px] text-gray-400">
      <span class="flex items-center gap-1.5"><ShieldCheck class="size-3.5 text-green-600" />Conforme</span>
      <span class="flex items-center gap-1.5"><TriangleAlert class="size-3.5 text-amber-600" />Couverture fragile</span>
      <span class="flex items-center gap-1.5"><ShieldAlert class="size-3.5 text-red-500" />Sans pharmacien</span>
    </div>
  </div>
</template>
